import * as React from "react";
import { type IColumn, Icon, IconButton } from "@fluentui/react";
import { makeObservable, computed, observable, runInAction } from "mobx";
import { observer } from "mobx-react";
import { type UUID } from "../../utils/uuid";
import {
    type PreOpsChecklist,
    type ShockProfile,
    type User,
    type Vehicle,
    type VehicleDevice,
    type VehicleGroup,
} from "../../api";
import { BooleanIcon } from "./boolean-icon";
import { mapSome } from "../../utils/functions";
import { defaultPageSize } from "../../utils/constants";
import dummyLeaseAgreement from "../../documents/lease_agreement.pdf";

export interface ListVehicleItemCellProps {
    readonly vehicleId: UUID;
    readonly column: IColumn;
    readonly filteredVehicleGroups: string[];
}

import { store } from "../../store";

@observer
export class ListVehicleItemCell extends React.Component<ListVehicleItemCellProps> {
    @observable private accessor lastDriver: User | undefined;
    @observable private accessor lastDriverChecked: boolean = false;

    constructor(props: ListVehicleItemCellProps) {
        super(props);
        makeObservable(this);
    }

    @computed private get vehicle(): Vehicle | undefined {
        return store.repositoryVehicles.byId(this.props.vehicleId);
    }

    @computed private get preOpsChecklist(): PreOpsChecklist | undefined {
        if (!this.vehicle || !this.vehicle.preOpsChecklistId) {
            return;
        }
        return store.repositoryPreOpsChecklists.byId(this.vehicle.preOpsChecklistId);
    }

    @computed private get shockProfile(): ShockProfile | undefined {
        if (!this.vehicle || !this.vehicle.shockProfileId) {
            return;
        }
        return store.repositoryShockProfiles.byId(this.vehicle.shockProfileId);
    }

    @computed private get vehicleDepartment(): string | undefined {
        if (!this.vehicle || !this.vehicle.departmentId) {
            return;
        }
        return store.repositoryDepartments.byId(this.vehicle.departmentId)?.label;
    }

    @computed private get vehicleType(): string | undefined {
        if (!this.vehicle || !this.vehicle.vehicleTypeId) {
            return;
        }
        return store.repositoryVehicleTypes.byId(this.vehicle.vehicleTypeId)?.label;
    }

    @computed private get groups(): VehicleGroup[] {
        if (!this.vehicle) {
            return [];
        }
        return store.repositoryVehicleGroups.byQuery({
            pageSize: defaultPageSize,
            vehicleId: this.vehicle.id,
        });
    }

    @computed private get vehicleDevice(): VehicleDevice | undefined {
        return store.repositoryVehicleDevices.byVehicleId(this.props.vehicleId);
    }

    private fetchLastDriver(): void {
        if (this.props.column.key === "lastDriver" && !this.lastDriverChecked) {
            store.repositoryUsers
                .getLastDriverByVehicle(this.vehicle!.id)
                .then((user) => runInAction(() => (this.lastDriver = user)))
                .finally(() => runInAction(() => (this.lastDriverChecked = true)));
        }
    }

    public formatHoursUntilNextMaintenance(hour: number | undefined): string {
        if (hour === undefined) {
            return "";
        }

        return `${store.i18n.formatHours(Math.abs(hour))} ${
            hour <= 0 ? store.i18n.t("listVehicles.overdue") : ""
        }`;
    }

    public render(): JSX.Element {
        const { column } = this.props;
        this.fetchLastDriver();

        switch (column.key) {
            case "eloboxId":
                if (!this.vehicleDevice) {
                    return <></>;
                }
                return <span>{this.vehicleDevice.serialNumber}</span>;
            case "phoneImei":
                return <span>{this.vehicle?.phoneImei}</span>;
            case "manufacturer":
                return <span>{this.vehicle?.manufacturer ?? ""}</span>;
            case "model":
                return <span>{this.vehicle?.model ?? ""}</span>;
            case "vehicleType":
                return <span>{this.vehicleType}</span>;
            case "engineType": {
                if (!this.vehicle?.engineType) {
                    return <></>;
                }
                const label = store.i18n.t(`listVehicles.engineType.${this.vehicle.engineType}`);
                return <span>{label}</span>;
            }
            case "serialNumber":
                return <span>{this.vehicle?.serialNumber}</span>;
            case "manufactureYear":
                return <span>{this.vehicle?.manufactureYear ?? ""}</span>;
            case "department":
                return <span>{this.vehicleDepartment}</span>;
            case "group":
                if (this.groups.length === 0) {
                    return <span />;
                }
                return (
                    <div>
                        {this.groups.map((group) => {
                            if (this.props.filteredVehicleGroups.includes(group.id)) {
                                return (
                                    <div key={group.id}>
                                        <strong>{group.label}</strong>
                                    </div>
                                );
                            }
                            return <div key={group.id}>{group.label}</div>;
                        })}
                    </div>
                );
            case "shockProfile":
                return <span>{this.shockProfile?.label ?? ""}</span>;
            case "checklistProfile":
                return <span>{this.preOpsChecklist?.label}</span>;
            case "checklistOverride":
                return this.vehicle?.checklistOverride !== undefined ? (
                    <BooleanIcon status={this.vehicle?.checklistOverride} />
                ) : (
                    <></>
                );
            case "initialOperatingHours":
                return this.vehicle?.initialOperatingHours ? (
                    <span>{store.i18n.formatHours(this.vehicle.initialOperatingHours)}</span>
                ) : (
                    <></>
                );
            case "idleAutoLogoutAfter":
                return <span></span>;
            case "maintenanceNotificationAt":
                return <span>{this.vehicle?.maintenanceNotificationAt ?? ""}</span>;
            case "maintenanceLockdown":
                return this.vehicle?.maintenanceLockdown !== undefined ? (
                    <BooleanIcon status={this.vehicle?.maintenanceLockdown} />
                ) : (
                    <></>
                );
            case "lastDriver":
                return this.lastDriver ? (
                    <span>{store.i18n.formatUserFullName(this.lastDriver)}</span>
                ) : (
                    <span />
                );
            case "lastSynchronization":
                return (
                    <span>
                        {this.vehicle?.lastSynchronizedAt
                            ? store.i18n.formatDateTime(this.vehicle.lastSynchronizedAt)
                            : ""}
                    </span>
                );
            case "lastMaintenanceDate":
                return (
                    <span>
                        {this.vehicle?.lastMaintenanceDate
                            ? store.i18n.formatDateOnly(this.vehicle.lastMaintenanceDate)
                            : ""}
                    </span>
                );
            case "lastMaintenanceHours": {
                return (
                    <span>
                        {this.vehicle?.lastMaintenanceOperatingHours
                            ? store.i18n.formatHours(this.vehicle.lastMaintenanceOperatingHours)
                            : ""}
                    </span>
                );
            }
            case "nextMaintenanceHours": {
                return (
                    <span>
                        {this.vehicle?.nextMaintenanceHours
                            ? store.i18n.formatHours(this.vehicle.nextMaintenanceHours)
                            : ""}
                    </span>
                );
            }
            case "hoursUntilMaintenance": {
                return (
                    <span>
                        {this.formatHoursUntilNextMaintenance(
                            this.vehicle?.hoursUntilNextMaintenance,
                        )}
                    </span>
                );
            }
            case "purchaseTermBegin": {
                const startDate = this.vehicle?.contractTermStart ?? this.vehicle?.purchaseDate;
                return <span>{startDate ? store.i18n.formatDateOnly(startDate) : ""}</span>;
            }
            case "contractTermEnd":
                return (
                    <span>
                        {this.vehicle?.contractTermEnd
                            ? store.i18n.formatDateOnly(this.vehicle.contractTermEnd)
                            : ""}
                    </span>
                );
            // This is just a placeholder for now and serves a static contract PDF.
            case "contractFile":
                return (
                    <IconButton
                        iconProps={{ iconName: "DownloadDocument" }}
                        href={dummyLeaseAgreement}
                        download="lease_agreement.pdf"
                    />
                );
            case "procurementType": {
                if (!this.vehicle?.engineType) {
                    return <></>;
                }
                const label = this.vehicle.procurementType
                    ? store.i18n.t(`listVehicles.procurementType.${this.vehicle.procurementType}`)
                    : "";
                return <span>{label}</span>;
            }
            case "purchaseCost":
                return (
                    <span>
                        {this.vehicle?.purchaseCost
                            ? store.i18n.formatCurrency(this.vehicle.purchaseCost)
                            : ""}
                    </span>
                );
            case "monthlyCost":
                return (
                    <span>
                        {this.vehicle?.monthlyCost
                            ? store.i18n.formatCurrency(this.vehicle.monthlyCost)
                            : ""}
                    </span>
                );
            case "status":
                return (
                    <span>
                        {mapSome(
                            (status) => store.i18n.t(`listVehicleItemCell.status.${status}`),
                            this.vehicle?.status,
                        )}
                    </span>
                );
            case "currentOperatingHours": {
                const seconds = this.vehicle?.totalOperatingSeconds;
                return <>{seconds ? store.i18n.formatDuration(seconds) : ""}</>;
            }
            case "vehicleStatus": {
                if (!this.vehicle?.status) {
                    return <></>;
                }
                const label = store.i18n.t(`listVehicles.status.${this.vehicle.status}`);
                return <span>{label}</span>;
            }
            case "maintenanceInterval":
                return <span>{this.vehicle?.maintenanceInterval ?? ""}</span>;
            case "daysUntilContractTermEnd": {
                const contractTermEnd = this.vehicle?.contractTermEnd;
                if (!contractTermEnd) {
                    return <></>;
                }
                const difference = Math.max(0, contractTermEnd.getTime() - new Date().getTime());
                const millisecondsPerDay = 1000 * 60 * 60 * 24;
                const days = Math.floor(difference / millisecondsPerDay);
                return <span>{store.i18n.addThousandsSeperator(days)}</span>;
            }
            case "vehicleIdleAutoLogoutAfter":
                return (
                    <span>
                        {this.vehicle?.idleAutoLogoutAfter != null
                            ? store.i18n.formatMinutes(this.vehicle?.idleAutoLogoutAfter)
                            : ""}
                    </span>
                );
            default:
                if (this.groups.find((group) => group.id === column.key)) {
                    return (
                        <span style={{ display: "block", textAlign: "center" }}>
                            <Icon iconName="Accept" />
                        </span>
                    );
                }
                console.warn(`Unknown column ${column.key} in ListVehicleItemCell`);
                return <></>;
        }
    }
}
